import { useCallback, useEffect, useRef } from "react";
import { supabase } from "@/integrations/supabase/client";
import type { Json } from "@/integrations/supabase/types";

interface TrackingData {
  interaction_type: string;
  customer_name?: string;
  customer_email?: string;
  customer_phone?: string;
  source_page?: string;
  metadata?: Json;
}

// Generate a unique session ID
const getSessionId = (): string => {
  let sessionId = sessionStorage.getItem("ph_session_id");
  if (!sessionId) {
    sessionId = `sess_${Date.now()}_${Math.random().toString(36).substring(2, 9)}`;
    sessionStorage.setItem("ph_session_id", sessionId);
  }
  return sessionId;
};

// Get UTM parameters from URL
const getUtmParams = (): { utm_source?: string; utm_medium?: string; utm_campaign?: string } => {
  const params = new URLSearchParams(window.location.search);
  return {
    utm_source: params.get("utm_source") || undefined,
    utm_medium: params.get("utm_medium") || undefined,
    utm_campaign: params.get("utm_campaign") || undefined,
  };
};

export const useInteractionTracker = () => {
  const sessionId = useRef<string>(getSessionId());
  const utmParams = useRef(getUtmParams());

  // Track on mount for page views
  useEffect(() => {
    // Store UTM params if present
    const params = getUtmParams();
    if (params.utm_source) {
      utmParams.current = params;
    }
  }, []);

  const trackInteraction = useCallback(async (data: TrackingData) => {
    try {
      const insertData: {
        interaction_type: string;
        customer_name?: string | null;
        customer_email?: string | null;
        customer_phone?: string | null;
        source_page?: string | null;
        session_id?: string | null;
        utm_source?: string | null;
        utm_medium?: string | null;
        utm_campaign?: string | null;
        metadata?: Json | null;
        status?: string | null;
      } = {
        interaction_type: data.interaction_type,
        customer_name: data.customer_name || null,
        customer_email: data.customer_email || null,
        customer_phone: data.customer_phone || null,
        source_page: data.source_page || window.location.pathname,
        session_id: sessionId.current,
        utm_source: utmParams.current.utm_source || null,
        utm_medium: utmParams.current.utm_medium || null,
        utm_campaign: utmParams.current.utm_campaign || null,
        metadata: data.metadata || null,
        status: "new",
      };

      const { error } = await supabase.from("customer_interactions").insert([insertData]);

      if (error) {
        console.error("Error tracking interaction:", error);
        return false;
      }

      return true;
    } catch (err) {
      console.error("Error in trackInteraction:", err);
      return false;
    }
  }, []);

  const trackFormSubmission = useCallback(
    async (formType: string, formData: { name?: string; email?: string; phone?: string; [key: string]: unknown }) => {
      const sanitizedData: Json = {
        name: formData.name || null,
        email: formData.email || null,
        phone: formData.phone || null,
      };
      return trackInteraction({
        interaction_type: `form_submission_${formType}`,
        customer_name: formData.name,
        customer_email: formData.email,
        customer_phone: formData.phone,
        metadata: sanitizedData,
      });
    },
    [trackInteraction]
  );

  const trackWhatsAppClick = useCallback(
    async (context?: string) => {
      return trackInteraction({
        interaction_type: "whatsapp_click",
        metadata: context ? { context } : null,
      });
    },
    [trackInteraction]
  );

  const trackCartAction = useCallback(
    async (action: "add" | "remove" | "checkout" | "abandon", itemDetails?: Record<string, unknown>) => {
      const safeMetadata: Json | null = itemDetails 
        ? JSON.parse(JSON.stringify(itemDetails)) as Json
        : null;
      return trackInteraction({
        interaction_type: `cart_${action}`,
        metadata: safeMetadata,
      });
    },
    [trackInteraction]
  );

  return {
    trackInteraction,
    trackFormSubmission,
    trackWhatsAppClick,
    trackCartAction,
    sessionId: sessionId.current,
  };
};

export default useInteractionTracker;
